
const wikiData = [
    {
        id: 1,
        title: "Welcome to Vex Conquer",
        icon: "🔥",
        content: `
Welcome to ** Vex Conquer **, warrior.

Vex Conquer is built around the classic Conquer experience with a focused ** 5165 - style gameplay system **, familiar classes, traditional PvP, guild battles, hunting, trading, and progression.

### Before You Begin

1. ** Create your character ** and choose the class that matches your preferred playstyle.
2. ** Explore Twin City ** and familiarize yourself with the important NPCs.
3. ** Follow the early quests ** to gain experience, equipment, and useful rewards.
4. ** Learn the server systems ** before spending your CPS or upgrade materials.
5. ** Join a guild ** when you are ready to participate in group activities.
6. Keep an eye on ** Server News ** and ** Events ** for announcements and special activities.

### Vex Conquer Basics

    - ** Classic 5165 gameplay **
- ** Maximum Level: 130 **
- ** 1st Rebirth **
- ** Maximum Equipment Plus: +9 **
- ** Bless System **
- ** Classic PvP **
- ** Guild Wars **
- ** CPS & Shopping Mall **
- ** Player Marketplace **
- ** Boss Hunts & Quests **

    Take your time, learn the server, and build your character your own way.

** Your character.Your guild.Your legacy.**
    `,
    },

    {
        id: 2,
        title: "Server Overview",
        icon: "🏰",
        content: `
### The Vex Conquer Experience

Vex Conquer is designed for players who enjoy the classic Conquer style without losing the convenience of modern server features.

The goal is simple: build your character, improve your equipment, find your people, and compete.

### Server Progression

    - ** Level Cap:** 130
        - ** Rebirth:** 1st Rebirth
            - ** Maximum Plus:** +9
                - ** Classes:** Trojan, Warrior, Archer, Water Taoist, Fire Taoist
                    - ** Ninja:** Not available
                        - ** Monk:** Not available
                            - ** PvP:** Classic - style PvP
                                - ** Economy:** Silver, CPS, equipment and player trading
                                    - ** Guild System:** Available
                                        - ** Events:** Regular server activities and competitions

### Start Smart

Do not rush into spending every resource you obtain.

Learn which equipment is worth upgrading, understand the marketplace, and save valuable materials for the upgrades that matter most to your build.

Vex Conquer rewards players who understand their character rather than simply collecting items.
        `,
    },

    {
        id: 3,
        title: "New Players Guide",
        icon: "⚔️",
        content: `
### Welcome, Warrior

Starting a new character can feel overwhelming, especially if you have not played classic Conquer for a while.

Use this simple progression path to get comfortable.

### Step 1 — Create Your Character

Choose a class based on how you want to play.

- ** Trojan:** Close - range damage and aggressive PvP.
- ** Warrior:** Defensive gameplay with strong protection.
- ** Archer:** Ranged attacks and efficient hunting.
- ** Water Taoist:** Support, healing, and utility.
- ** Fire Taoist:** Offensive magic and ranged spell damage.

### Step 2 — Learn Twin City

Twin City is one of the most important places for new players.

Spend some time learning where the important NPCs, shops, storage, teleport options, and other services are located.

### Step 3 — Build Your First Set

Do not worry about creating a perfect character immediately.

Start with practical equipment and improve it as you progress.

### Step 4 — Learn the Economy

Understand the difference between:

-Silver
    - CPS
    - Equipment
    - Upgrade materials
        - Marketplace items

Knowing the value of your resources will save you from unnecessary spending.

### Step 5 — Find a Guild

A good guild can make progression much easier.

You can hunt together, participate in guild activities, learn from experienced players, and eventually compete in Guild Wars.

** Tip:** Use **/help** to check the available commands on your character.
    `,
    },

    {
        id: 4,
        title: "Classes & Builds",
        icon: "🛡️",
        content: `
### Choose Your Fighting Style

Vex Conquer offers the classic class lineup with Water and Fire Taoist paths.

There is no single class that is perfect for every situation.Your equipment, build, experience, and playstyle all matter.

### Trojan ⚔️

Trojan is a close - range fighter focused on aggressive combat.

** Good for:**
    -PvP
    - Close - range battles
        - High - pressure combat
            - Players who enjoy aggressive gameplay

### Warrior 🛡️

Warriors are built around defense and survivability.

** Good for:**
    -Defensive play
        - Front - line combat
            - Players who prefer durability
                - Group battles

### Archer 🏹

Archers attack from range and are excellent for hunting and farming.

** Good for:**
        -PvE
        - Monster hunting
            - Farming
            - Ranged combat

### Water Taoist 💧

Water Taoists provide support, healing, and utility.

** Good for:**
    -Supporting teammates
        - Recovery
        - Group activities
            - Strategic PvP

### Fire Taoist 🔥

Fire Taoists specialize in magical offense.

** Good for:**
    -Ranged magic attacks
        - Area pressure
            - Offensive PvP
                - Players who prefer spell - based combat

Choose the class you enjoy.Equipment can change, but your understanding of your class is what makes the difference.
        `,
    },

    {
        id: 5,
        title: "Leveling & Progression",
        icon: "📈",
        content: `
### Build Your Character Step by Step

Progression in Vex Conquer is about becoming stronger without wasting your resources.

Your main goals should be:

** Level → Equipment → Skills → Resources → PvP **

### Early Game

Focus on completing available quests and gaining enough experience to unlock stronger hunting areas.

Do not spend valuable upgrade materials too early unless the equipment will remain useful for your character.

### Mid Game

Once your character becomes stronger, begin focusing on:

-Better equipment
    - Equipment upgrades
        - Gems
        - Bless
        - Hunting efficiency
            - CPS management

### Level 120–130

At higher levels, efficient hunting becomes increasingly important.

Look for areas where your character can defeat monsters consistently while maintaining a good experience and loot rate.

### Rebirth

The server supports ** 1st Rebirth **.

Plan your progression before rebirthing so you understand what changes and what you need to prepare.

** Remember:** A higher level does not automatically mean a stronger PvP character.Equipment and build decisions matter.
        `,
    },

    {
        id: 6,
        title: "Equipment & Upgrades",
        icon: "⚒️",
        content: `
### Build Your Gear

Equipment is one of the most important parts of your character.

Before upgrading an item, consider how long you expect to use it.

### Upgrade Priorities

A practical approach is to improve the equipment that gives your character the biggest immediate benefit.

Pay attention to:

-Equipment level
    - Quality
    - Plus
    - Bless
    - Gems
    - Weapon setup
        - Character class

### Maximum Plus

Vex Conquer uses a ** +9 maximum equipment system **.

Do not burn through all your resources chasing upgrades without a plan.

### Gems

Gems can significantly improve your equipment.

Before using valuable gems, think about whether the item is worth keeping long term.

### Upgrade Materials

Keep track of your upgrade materials instead of spending everything immediately.

A player with resources available for the right upgrade can often progress faster than someone who spends everything as soon as it appears.

** Tip:** Compare the cost of an upgrade with the actual benefit it provides to your character.
        `,
    },

    {
        id: 7,
        title: "Hunting & Farming",
        icon: "🏹",
        content: `
### Hunt With a Purpose

Monster hunting is more than simply gaining experience.

Different hunting areas can provide different opportunities for experience, silver, equipment, and valuable drops.

### Before Hunting

Make sure you have:

-Enough HP and Mana recovery
    - Suitable equipment
        - Enough arrows or required supplies
            - Free inventory space
                - A clear target for your farming session

### Efficient Farming

Choose a hunting location where your character can defeat monsters comfortably.

If you spend most of your time traveling, recovering, or struggling against monsters, another location may be more efficient.

### Elite Monsters & Bosses

Stronger monsters can provide better opportunities, but they also come with greater risk.

Bring friends when appropriate and be prepared for competition from other players.

### Marketplace

Valuable drops do not always need to be sold immediately.

Check the ** Marketplace ** first and compare what other players are asking for similar items.

Sometimes the best profit comes from knowing when to sell.
        `,
    },

    {
        id: 8,
        title: "Boss Hunts",
        icon: "🐉",
        content: `
### Hunt the Big Targets

Boss hunting is one of the more competitive activities in Vex Conquer.

Bosses can attract multiple players, guilds, and rival hunters, so preparation matters.

### Known Boss Areas

Some of the server's known boss-related locations include:

    - ** Terato **
- ** DBDevils **
- ** MetsDove **
- ** SnowBanshee **
    -Other server - specific boss encounters

Exact locations, spawn times, and availability may change.

Always check ** Server News **, in -game announcements, or ask other players for the latest information.

### Boss Hunting Tips

    - Arrive prepared.
- Keep your character supplied.
- Watch your surroundings.
- Bring guild members or friends when necessary.
- Do not assume another player will leave the boss uncontested.
- Be ready for PvP around valuable targets.

Boss hunting is not only about damage.Timing, positioning, and awareness can be just as important.
        `,
    },

    {
        id: 9,
        title: "Mining & Resources",
        icon: "⛏️",
        content: `
### Mine for Resources

Mining can be a useful way to collect resources while doing something different from normal monster hunting.

Visit the appropriate mining area and use the required mining equipment.

### Mining Basics

1. Travel to the mining area.
2. Equip your Pickaxe.
3. Start mining.
4. Monitor your inventory.
5. Keep useful resources and sell or trade what you do not need.

You may encounter different ores, gems, and other resources depending on the server's current configuration.

### Stay Alert

Mining areas can attract other players.

Do not assume that every location is completely safe from player activity.

Keep your character prepared and avoid carrying unnecessary valuables when you do not need them.
        `,
    },

    {
        id: 10,
        title: "CPS & Shopping Mall",
        icon: "💰",
        content: `
### Understanding CPS

CPS is an important part of the Vex Conquer economy.

Depending on the current server configuration, CPS can be used for various services and Shopping Mall items.

### Spend Wisely

Before purchasing something, ask yourself:

-Do I actually need it ?
    -Will it improve my character ?
        -Is there a cheaper alternative ?
            -Can I obtain the item through gameplay ?
                -Is the item useful for my current build ?

### Shopping Mall

The Shopping Mall provides convenient access to various server items and services.

Always check the current prices before spending CPS because server offers and promotions may change.

### CPS Management

Do not spend your entire CPS balance just because you have enough to purchase something.

Saving CPS for an important upgrade, event, or useful item can make a significant difference later.

** Smart spending is part of progression.**
    `,
    },

    {
        id: 11,
        title: "Trading & Marketplace",
        icon: "💼",
        content: `
### Player Trading

Trading is a major part of the Vex Conquer economy.

Players can exchange equipment, resources, and other valuable items.

### Marketplace

Use the Marketplace to discover items listed by other players.

Before buying:

-Check the item's quality.
    - Check its Plus.
- Check gems and other attributes.
- Compare several listings.
- Confirm the price before completing the transaction.

### Avoid Mistakes

Never rush a trade because someone tells you that an offer will disappear.

    Double - check the item and amount before confirming.

If you are unsure about an item's value, ask experienced players or compare Marketplace listings first.

### Important

Vex Conquer staff will not necessarily be able to reverse every player - to - player trading mistake.

** Check before you click.**
    `,
    },

    {
        id: 12,
        title: "Guilds & Guild Wars",
        icon: "🏰",
        content: `
### Find Your Guild

A guild can completely change your Vex Conquer experience.

Guild members can hunt together, share knowledge, participate in activities, and prepare for Guild Wars.

### Choosing a Guild

Look beyond the guild name.

    Consider:

-Activity
    - Leadership
    - Communication
    - PvP participation
        - Guild goals
            - Community attitude

### Guild Wars

Guild Wars are competitive battles where teamwork and organization matter.

Before entering a Guild War:

-Repair your equipment.
- Prepare your supplies.
- Know your role.
- Communicate with your guild.
- Follow your leader's strategy.
    - Keep track of objectives.

A strong guild is not simply a collection of strong characters.

** Coordination wins battles.**
    `,
    },

    {
        id: 13,
        title: "PvP Guide",
        icon: "⚡",
        content: `
### Enter the Battlefield

Vex Conquer keeps the focus on classic - style PvP.

Winning a fight is not only about having powerful equipment.

### Learn Your Opponent

Pay attention to:

-Class
    - Equipment
    - Position
    - Movement
    - Skill timing
        - Health
        - Defensive abilities

### PvP Fundamentals

    ** Positioning ** matters.

Do not stand in a bad position simply because your opponent is there.

** Timing ** matters.

Using an ability at the wrong moment can turn a winning fight into a loss.

** Awareness ** matters.

Always know what is happening around you, especially during group battles.

### Practice

The fastest way to improve is to fight.

Learn from your mistakes, experiment with your build, and understand what other classes can do.

** Gear gets you into the fight.Skill keeps you there.**
    `,
    },

    {
        id: 14,
        title: "VIP Features",
        icon: "💎",
        content: `
### VIP Benefits

VIP provides additional quality - of - life features designed to make certain parts of the game more convenient.

Current VIP features may include:

- ** Remote Warehouse **
- ** Auto Loot **
- ** VIP Maps **
- ** VIP Experience Bonuses **
- **/scroll command**
    - Additional drop - related benefits

### VIP Levels

Some features and bonuses may depend on your VIP level.

Always check the current server configuration or VIP information before purchasing or upgrading VIP.

### Important

VIP is designed to provide convenience and additional benefits.

It does not replace learning your class, improving your equipment, or understanding the game.

Check the official Vex Conquer website for the latest VIP details.
        `,
    },

    {
        id: 15,
        title: "Commands",
        icon: "⌨️",
        content: `
### Useful Commands

Vex Conquer provides several commands that can make everyday gameplay easier.

### General Commands

    - **/help** — Display available commands.
    - **/stuck** — Use when your character becomes stuck or encounters a black-screen issue.
    - **/discord** — Open the server's Discord information.
    - **/website** — Open the Vex Conquer website.
    - **/dc** — Disconnect your character.

### Teleport & Utility

    - **/scroll** — Teleport using the available city destinations.
    - **/joinpvp** — View available or upcoming PvP activities.
    - **/rolescale** — Adjust your character's viewing scale.
    - **/vipmineores** — Use the available VIP mining utility.
    - **/screenshot** — Capture a screenshot.

### Important

Some commands may have cooldowns, requirements, or restrictions.

If a command does not work, check the latest server information or contact support.
        `,
    },

    {
        id: 16,
        title: "Events & Activities",
        icon: "🎉",
        content: `
### There Is Always Something Happening

Events are one of the best ways to take a break from normal grinding and compete for rewards, recognition, or simply bragging rights.

Depending on the current event schedule, Vex Conquer may feature:

-Experience events
    - Guild Wars
        - PvP activities
            - Special quests
                - Boss activities
                    - Seasonal events
                        - Community competitions
                            - Limited - time promotions

### Stay Updated

Event times can change.

Always check the ** Events ** page, ** Server News **, in -game announcements, and official Discord for the latest schedule.

### Event Tip

Do not wait until the last minute.

Prepare your equipment, supplies, and character before the event begins.
        `,
    },

    {
        id: 17,
        title: "Server Rules",
        icon: "📜",
        content: `
### Keep Vex Conquer Fair

Every player is responsible for understanding and following the server rules.

### 1. Cheating & Exploiting

Using unauthorized third - party tools, hacks, or intentionally abusing game bugs may result in punishment, including permanent account termination.

If you discover an exploit, report it instead of abusing it.

### 2. Account Sharing

You are responsible for your account.

If you voluntarily share your password and your account or items are lost, stolen, or transferred by another person, staff may not be able to recover them.

Never give your password to another player.

### 3. Real - Money Trading

Private Real - Money Trading is allowed at your own risk.

    However, advertising or publicly promoting RMT through:

-World Chat
    - Broadcasts
    - Public channels
        - Public Discord channels

is prohibited.

The current punishment for public RMT advertising may include a ** 1 - week mute for a first offense **, with stronger action for repeated violations.

### 4. Ban Evasion

If you receive a temporary punishment, wait until it expires.

Attempting to bypass a temporary ban may result in additional punishment.

Players who believe a permanent punishment was issued incorrectly may contact a staff member and request a review.

### Enforcement

Punishments depend on the severity and circumstances of the violation.

Possible actions include warnings, mutes, temporary suspensions, or permanent bans.

Play responsibly and help keep Vex Conquer enjoyable for everyone.
        `,
    },

    {
        id: 18,
        title: "Account Security",
        icon: "🔐",
        content: `
### Protect Your Account

Your account is your responsibility.

The best protection is simple: never give your password to another player.

### Never Share

Do not share:

-Passwords
    - Recovery information
        - Verification codes
            - Private account information

Staff members should never need your password to investigate an issue.

### Trading Security

Before confirming a trade:

1. Check the item.
2. Check the quantity.
3. Check the equipment attributes.
4. Confirm the other player's name.
5. Make sure everything is correct before accepting.

### Suspicious Activity

If someone claims to be staff and asks for your password, treat the request as suspicious.

Contact the official Vex Conquer support channels instead.

** A few seconds of checking can save your entire character.**
    `,
    },

    {
        id: 19,
        title: "New Player Checklist",
        icon: "✅",
        content: `
### Your First Steps

Use this checklist when starting your Vex Conquer journey.

☐ Create your character.

☐ Learn your class and basic skills.

☐ Explore Twin City.

☐ Learn the important NPC locations.

☐ Complete beginner content.

☐ Start building your equipment.

☐ Learn how Plus, Bless, and Gems work.

☐ Learn how CPS and Silver are used.

☐ Check the Marketplace.

☐ Join the official Discord.

☐ Find an active guild.

☐ Read the server rules.

☐ Check the Events schedule.

☐ Read Server News regularly.

### Most Important Advice

Do not compare your character to players who have been playing longer than you.

Build at your own pace, learn the game, and improve one upgrade at a time.

Everyone starts somewhere.
        `,
    },

    {
        id: 20,
        title: "Help & Support",
        icon: "🆘",
        content: `
### Need Assistance ?

    If something is not working correctly or you have a question about your account, use the official support channels.

### Discord Support

Join the official ** Vex Conquer Discord ** for community assistance, announcements, and support.

Check the appropriate help or support channel before creating a new request.

### Account Problems

For account - related concerns, you may contact:

** VexConquer2025@gmail.com**

    When contacting support, provide useful information about the problem without sharing your password.

### Bug Reports

Found a bug ?

    Tell the staff what happened and include useful details such as:

-What you were doing
    - Where it happened
        - What you expected to happen
            - What actually happened
                - Screenshots or other useful evidence

### Exploit Reports

If you discover something that gives players an unintended advantage, do not abuse it.

Report it privately so it can be investigated.

** Help us fix the problem instead of becoming part of it.**
    `,
    },

    {
        id: 21,
        title: "Frequently Asked Questions",
        icon: "❓",
        content: `
### How do I create an account ?

    Use the ** Register ** option on the Vex Conquer website.Complete the required information and follow the registration instructions.

### What is the maximum level ?

    The current level cap is ** 130 **.

### Does Vex Conquer have rebirth ?

    Yes.The server supports ** 1st Rebirth **.

### What classes are available ?

    The classic class lineup includes:

-Trojan
    - Warrior
    - Archer
    - Water Taoist
        - Fire Taoist

Ninja and Monk are not part of the current class lineup.

### What is the maximum Plus ?

    The current maximum equipment Plus is **+9 **.

### Can I transfer my character from another server ?

    No.Character and item transfers from other servers are not supported.Every player starts their Vex Conquer journey here.

### Where can I find events ?

    Check the ** Events ** page, in -game announcements, Server News, and the official Discord.

### What should I do if I find an exploit ?

    Do not abuse it.Report it to the staff through the official support channels.

### What should I do if I forget my password ?

    Use the password recovery option available on the website.Make sure you still have access to the required account recovery information.

### Where can I get help ?

    The official Vex Conquer Discord is the best place to start for community and server support.

If your issue involves your account, contact the appropriate staff / support channel.
        `,
    },

    {
        id: 22,
        title: "Vex Conquer Tips",
        icon: "💡",
        content: `
### Small Decisions Matter

You do not need to know everything on your first day.

Here are a few habits that will help you progress.

### Do Not Waste Resources

Before using an expensive material, ask whether the item is something you will keep.

### Check Prices

Never assume an item is worth a certain amount.

Compare Marketplace listings and ask experienced players when necessary.

### Keep Useful Items

Some materials that appear unimportant early in the game can become valuable later.

Do not throw everything away without checking what it is used for.

### Learn Your Class

A player who understands their class can often outperform someone with better equipment but poor decision - making.

### Watch Server News

Server changes, events, fixes, and promotions can affect the way you play.

### Make Friends

Vex Conquer is a multiplayer game.

A good group of players can make hunting, PvP, Guild Wars, and everyday gameplay much more enjoyable.

### Most Important

Do not measure your entire experience by how quickly you reach the strongest equipment.

Enjoy the journey.

** The character you build is only part of the story.The people and battles you remember are the rest.**
    `,
    },
];

export default wikiData;

